<template>
  <div class="chatbot-input">
    <div class="input-container">
      <textarea
        ref="inputRef"
        v-model="inputText"
        placeholder="Ask a question..."
        rows="1"
        :disabled="isLoading"
        @keydown.enter.exact.prevent="handleSend"
        @input="autoResize"
      />
      <button
        class="send-btn"
        :disabled="!inputText.trim() || isLoading"
        aria-label="Send message"
        @click="handleSend"
      >
        <i :class="isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-send'" />
      </button>
    </div>
    <div class="input-hint">
      Press Enter to send
    </div>
  </div>
</template>

<script setup lang="ts">
const { sendMessage, isLoading } = useChatbot()

const inputText = ref('')
const inputRef = ref<HTMLTextAreaElement | null>(null)

async function handleSend() {
  const text = inputText.value.trim()
  if (!text || isLoading.value) return

  inputText.value = ''
  resetHeight()
  await sendMessage(text)
}

function autoResize() {
  if (!inputRef.value) return
  inputRef.value.style.height = 'auto'
  const maxHeight = 100
  inputRef.value.style.height = `${Math.min(inputRef.value.scrollHeight, maxHeight)}px`
}

function resetHeight() {
  if (!inputRef.value) return
  inputRef.value.style.height = 'auto'
}
</script>

<style scoped>
.chatbot-input {
  padding: 12px 16px;
  background: var(--p-surface-800);
  border-top: 1px solid rgba(57, 255, 20, 0.1);
}

.input-container {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: var(--p-surface-900);
  border: 1px solid rgba(57, 255, 20, 0.2);
  border-radius: 16px;
  padding: 8px 12px;
  transition: border-color 0.2s ease;
}

.input-container:focus-within {
  border-color: var(--p-primary-500);
  box-shadow: 0 0 10px rgba(57, 255, 20, 0.1);
}

.input-container textarea {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  color: var(--p-surface-100);
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 4px 0;
  font-family: inherit;
  max-height: 100px;
}

.input-container textarea::placeholder {
  color: var(--p-surface-500);
}

.input-container textarea:disabled {
  opacity: 0.6;
}

.send-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-600));
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--p-surface-900);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 0 15px rgba(57, 255, 20, 0.4);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.send-btn i {
  font-size: 0.875rem;
}

.input-hint {
  text-align: center;
  font-size: 0.7rem;
  color: var(--p-surface-600);
  margin-top: 6px;
}
</style>
